/**
 * Mic permission denied recovery view.
 *
 * Shows browser-specific step-by-step instructions for re-enabling
 * microphone access, detected from navigator.userAgent.
 *
 * @module MicPermissionDenied
 */

import { useTranslation } from 'react-i18next';
import { MicOff, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

type BrowserVariant = 'chrome' | 'firefox' | 'safari' | 'generic';

function detectBrowser(): BrowserVariant {
  if (typeof navigator === 'undefined') return 'generic';
  const ua = navigator.userAgent.toLowerCase();
  // Edge is Chromium-based — Chrome steps work for it too
  if (ua.includes('chrome') || ua.includes('edg')) return 'chrome';
  if (ua.includes('firefox')) return 'firefox';
  if (ua.includes('safari')) return 'safari';
  return 'generic';
}

interface MicPermissionDeniedProps {
  /** Called when the user clicks "Try again" — may be async */
  readonly onRetry: () => void | Promise<void>;
}

export function MicPermissionDenied({ onRetry }: MicPermissionDeniedProps) {
  const { t } = useTranslation();
  const browser = detectBrowser();

  const browserTitle = t(
    `simulation.speaking.micSetup.denied.${browser}.title`,
  );
  const steps = t(
    `simulation.speaking.micSetup.denied.${browser}.steps`,
    { returnObjects: true },
  ) as string[];

  return (
    <div className="flex flex-col items-center gap-5 w-full">
      {/* Icon + heading */}
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
          <MicOff className="w-6 h-6 text-destructive" />
        </div>
        <h3 className="font-bold text-base text-foreground">
          {t('simulation.speaking.micSetup.denied.heading')}
        </h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          {t('simulation.speaking.micSetup.denied.description')}
        </p>
      </div>

      {/* Browser-specific steps */}
      <div className="w-full max-w-sm bg-muted/40 rounded-xl p-4 border border-border/60">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          {browserTitle}
        </p>
        <ol className="space-y-2">
          {Array.isArray(steps) &&
            steps.map((step, idx) => (
              <li key={idx} className="flex items-start gap-2.5 text-sm text-foreground">
                <span className="flex-none w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center mt-0.5">
                  {idx + 1}
                </span>
                <span className="leading-snug">{step}</span>
              </li>
            ))}
        </ol>
      </div>

      {/* Try again */}
      <Button
        variant="outline"
        size="sm"
        onClick={onRetry}
        className="gap-2"
      >
        <RefreshCw className="w-4 h-4" />
        {t('simulation.speaking.micSetup.denied.tryAgain')}
      </Button>
    </div>
  );
}
